<template>
<div>
  <h1>{{`${profileLabel[0].toUpperCase()}${profileLabel.substring(1, profileLabel.length)}`}}</h1>
  <p><i>Developer</i> profile syncronizes the data only to <u>developer phones</u>. <i>Production</i> profile pushes data to <b>all</b> users.</p>
  <el-dropdown
    :options="profiles"
    v-model="profileId"
    />
  <el-delimiter />

  <!-- Loading -->
  <el-message
    v-if="isLoadingSettings"
    :warning="true"
    >
    <h1>Loading settings...</h1>
    <p v-if="isDevMode">Loading developer settings.</p>
    <p v-else>Loading production settings.</p>
  </el-message>

  <!-- Angle settings -->
  <h1>Angles</h1>
  <div
    v-for="(angleSet, mode) in angles"
    :key="mode"
    >
    <h2>{{mode}}</h2>
    <el-inline>
      <div
        class="angleWrapper"
        v-for="(model, index) in angleSet"
        :key="`${mode}_${index}`"
        >
        <el-dropdown
          :options="availableAngles"
          v-model="angleSet[index]"
          @change="changeAngles()"
          />
        <p>{{getDifficultyLabel(index)}}</p>
      </div>
    </el-inline>
  </div>

  <br>
  <el-inline :margin-top="true">
    <el-button
      :margin-right="true"
      :disabled="!hasChanged.angles"
      @click="showConfirm = 'angles'"
      >
      Update
    </el-button>
    <el-button
      :secondary="true"
      :danger="true"
      :disabled="!hasChanged.angles"
      @click="reset()"
      >
      Reset
    </el-button>
  </el-inline>
  <el-delimiter />

  <!-- Intermediate level -->
  <h1>Intermediate difficulty</h1>
  <p>At what difficulty level should the app display "Intermediate"?</p>
  <el-multi-switch
    :options="meanLevelOptions"
    v-model="meanLevel"
    @change="hasChanged.meanLevel = true"
    />
  <br>
  <el-inline :margin-top="true">
    <el-button
      :margin-right="true"
      :disabled="!hasChanged.meanLevel"
      @click="showConfirm = 'meanLevel'"
      >
      Update
    </el-button>
    <el-button
      :secondary="true"
      :danger="true"
      :disabled="!hasChanged.meanLevel"
      @click="reset()"
      >
      Reset
    </el-button>
  </el-inline>

  <br>
  <el-delimiter />

  <!-- Device configuration -->
  <h1>Device configuration</h1>
  <el-inline
    v-for="(config, type) in deviceConfiguration"
    :key="type"
    >
    <p v-if="units[type]" style="width: 300px;">{{type}} [{{units[type]}}]</p>
    <p v-else style="width: 300px;">{{type}}</p>

    <el-input
      style="width: auto;"
      v-model="deviceConfiguration[type]"
      @change="change('deviceConfiguration')"
      :warning="!isNumber(deviceConfiguration[type])"
      />
  </el-inline>

  <el-inline :margin-top="true">
    <el-button
      :margin-right="true"
      :disabled="!hasChanged.deviceConfiguration"
      @click="showConfirm = 'deviceConfiguration'"
      >
      Update
    </el-button>
    <el-button
      :secondary="true"
      :danger="true"
      :disabled="!hasChanged.deviceConfiguration"
      @click="reset()"
      >
      Reset
    </el-button>
  </el-inline>
  <el-delimiter />

  <!-- Mode configuration -->
  <h1>Mode configuration</h1>
  <p>Combined configuration for Mode 1, 2 & 3</p>
  <el-inline
    v-for="(config, type) in levelConfiguration"
    :key="type"
    >
    <p v-if="units[type]" style="width: 300px;">{{type}} [{{units[type]}}]</p>
    <p v-else style="width: 300px;">{{type}}</p>
    <el-input
      style="width: auto;"
      v-model="levelConfiguration[type]"
      @change="change('levelConfiguration')"
      :warning="!isNumber(levelConfiguration[type])"
      />
  </el-inline>

  <el-inline :margin-top="true">
    <el-button
      :margin-right="true"
      :disabled="!hasChanged.levelConfiguration"
      @click="showConfirm = 'levelConfiguration'"
      >
      Update
    </el-button>
    <el-button
      :secondary="true"
      :danger="true"
      :disabled="!hasChanged.levelConfiguration"
      @click="reset()"
      >
      Reset
    </el-button>
  </el-inline>
  <el-delimiter />

  <!-- Confirm changes -->
  <el-modal
    v-if="showConfirm !== ''"
    @close="showConfirm = ''"
    close-button-align="right"
    >
    <h1>Confirm update</h1>
    <p>Are you sure you want to update <b>{{showConfirm}}</b>?</p>

    <el-button
      @click="update(showConfirm)"
      :disabled="isPending"
      :margin-right="true"
      >
      Push changes to {{profileLabel}}
    </el-button>

    <el-message :info="true" :marginTop="true" v-if="isPending">
      Syncing...
    </el-message>
  </el-modal>

  <!-- Error -->
  <el-modal
    v-if="showErrorInChange"
    @close="showErrorInChange = false"
    close-button-align="right"
    >
    <h1>Error!</h1>
    <p>Something went wrong while syncing with the server.</p>
  </el-modal>

  <br><br>
</div>
</template>
<script>

import firestore from 'services/firestore.js'
import logScope from 'log-scope'

const log = logScope('HeadQSettings')

export default {
  data () {
    return {
      profileId: '',
      hasChanged: {
        angles: false,
        meanLevel: false,
        deviceConfiguration: false,
        levelConfiguration: false
      },
      showConfirm: '',
      showErrorInChange: false,
      isLoadingSettings: false,
      isPending: false,
      isDevMode: false,
      maxAngle: 45,
      minAngle: 0,
      angles: {},
      meanLevelOptions: [
        { value: 1, label: '1' },
        { value: 2, label: '2' },
        { value: 3, label: '3' },
        { value: 4, label: '4' },
        { value: 5, label: '5' },
        { value: 6, label: '6' },
        { value: 7, label: '7' },
        { value: 8, label: '8' },
        { value: 9, label: '9' },
        { value: 10, label: '10' }
      ],
      meanLevel: -1,
      deviceConfiguration: {},
      levelConfiguration: {}
    }
  },
  watch: {
    profiles: {
      deep: true,
      handler: 'preSelectProfile'
    },
    profileId () {
      if (!this.profile) {
        return
      }

      // Angles
      Object.entries(this.profile.angles)
        .forEach(([mode, angles]) => {
          this.angles[mode] = angles.map(angle => Number(angle))
        })

      // Intermediate level
      this.meanLevel = this.profile.meanLevel

      // Device config
      Object.entries(this.profile.deviceConfiguration)
        .forEach(([key, value]) => {
          this.deviceConfiguration[key] = String(value)
        })

      // Mode config
      Object.entries(this.profile.levelConfiguration)
        .forEach(([key, value]) => {
          this.levelConfiguration[key] = String(value)
        })
    }
  },
  computed: {
    profiles () {
      return this.headQ.map(profile => ({
        value: profile.id,
        label: profile.profile
      }))
    },
    profileLabel () {
      const profile = this.profiles
        .find(profile => profile.value === this.profileId)
      if (!profile) {
        return '-'
      }

      return `${profile.label[0].toUpperCase()}${profile.label.substring(1, profile.label.length)}`
    },
    headQ () {
      return this.$store.getters.headQ
    },
    profile () {
      return this.headQ.find(profile => profile.id === this.profileId)
    },
    units () {
      return this.profile ? this.profile.configUnits : {}
    },
    availableAngles () {
      let angles = [{
        value: null,
        label: '-- angle --'
      }]

      for (let i = this.minAngle; i <= this.maxAngle; i++) {
        angles.push({
          value: i,
          label: `${i}°`
        })
      }

      return angles
    }
  },
  mounted () {
    this.preSelectProfile()
  },
  methods: {
    preSelectProfile () {
      if (this.profileId !== '' || this.profiles.length === 0) {
        return
      }

      this.profileId = this.profiles[0].value
    },
    changeAngles () {
      Object.entries(this.angles)
        .forEach(([mode, angles]) => {
          this.angles[mode] = angles.map(angle => Number(angle))
        })

      log.debug('Angles updated', this.angles)
      this.hasChanged.angles = true
    },
    isNumber (value) {
      return !isNaN(Number(value)) || value === ''
    },
    change (type) {
      this.hasChanged[type] = true
      this.$forceUpdate()
    },
    getDifficultyLabel (index) {
      return {
        0: 'Easy',
        1: 'Intermediate',
        2: 'Hard'
      }[index] || ''
    },
    async update (attr) {
      const id = this.profileId
      let data
      this.isPending = true

      if (attr === 'levelConfiguration' || attr === 'deviceConfiguration') {
        data = { [attr]: {} }

        Object.entries(this[attr]).forEach(([key, value]) => {
          data[attr][key] = Number(value)
        })
      } else {
        data = { [attr]: this[attr] }
      }

      try {
        await firestore.set('headQ', id, data, { merge: true })
        log.success('Updated: ' + attr)
      } catch (err) {
        log.error('Could not update: ' + attr, err)
      }

      this.isPending = false
      this.showConfirm = ''
      this.clearChangedStatus()
    },
    reset () {
      const id = this.profileId
      this.profileId = ''

      this.$nextTick(() => {
        this.profileId = id

        this.clearChangedStatus()
      })
    },
    clearChangedStatus () {
      Object.entries(this.hasChanged)
        .forEach(([attr, _value]) => {
          this.hasChanged[attr] = false
        })
    }
  }
}
</script>
<style scoped lang="less">
  .angleWrapper {
    display: flex;
    flex-grow: 1;
    max-width: 200px;
    margin-right: 10px;
    flex-direction: column-reverse;
    justify-content: flex-end;

    p {
      margin: 0px;
    }
  }
</style>
